import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../CSS/mapview.css';
import apiClient from '../lib/api-client';
import * as bootstrap from 'bootstrap';

import Plot from 'react-plotly.js';
import { useTheme } from '../ThemeContext';
import { useWindowWidth } from '../lib/useWindowWidth';
import {
  buildWeeklyCustomerPeaks,
  formatPeakDate,
  getTopWeeklyCustomerStates,
  getWeeklyCustomerPeak
} from '../lib/weeklyCustomers';
import SignInPromptModal from './SignInPromptModal';

const FIPS_TO_ABBR = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA',
  '08': 'CO', '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL',
  '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN',
  '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME',
  '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
  '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
  '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI',
  '45': 'SC', '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT',
  '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI',
  '56': 'WY', '72': 'PR'
};
const ABBR_TO_FIPS = Object.entries(FIPS_TO_ABBR).reduce((acc, [fips, abbr]) => { acc[abbr] = fips; return acc; }, {});

const FIPS_TO_NAME = {
  '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas', '06': 'California',
  '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware', '11': 'District of Columbia', '12': 'Florida',
  '13': 'Georgia', '15': 'Hawaii', '16': 'Idaho', '17': 'Illinois', '18': 'Indiana',
  '19': 'Iowa', '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana', '23': 'Maine',
  '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota', '28': 'Mississippi',
  '29': 'Missouri', '30': 'Montana', '31': 'Nebraska', '32': 'Nevada', '33': 'New Hampshire',
  '34': 'New Jersey', '35': 'New Mexico', '36': 'New York', '37': 'North Carolina', '38': 'North Dakota',
  '39': 'Ohio', '40': 'Oklahoma', '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island',
  '45': 'South Carolina', '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas', '49': 'Utah',
  '50': 'Vermont', '51': 'Virginia', '53': 'Washington', '54': 'West Virginia', '55': 'Wisconsin',
  '56': 'Wyoming', '72': 'Puerto Rico'
};

const MapVisual = ({
  currentMetric,
  currentDataType,
  currentRegion,
  setCurrentRegion,
  setCurrentMetric,
  setCurrentDataType,
  setPredictionType,
  predictionData,
  onCountyClick,
  isToday,
  startDate,
  endDate,
  timezone,
  stateId,
  selectedDay,
  predictionType = 'daily',
  isAuthenticated = true,
  freeTopStatesCustomersOut = [],
  countiesList = [], // Correctly using prop
  statesList = [],   // Correctly using prop
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const geoJsonLayer = useRef(null);
  const polygonLayer = useRef(null);
  const labelMarkers = useRef([]); // Add ref to store label markers
  const initialPolygonBounds = useRef(null); // Add this new ref to store initial polygon bounds
  const lastFittedStateId = useRef(null); // Track the last state ID we fitted bounds for
  const activeTooltipLayer = useRef(null);
  const weeklyPointClickRef = useRef(false);
  const lastWeeklyStateClickRef = useRef({ fips: null, timestamp: 0 });
  const currentTileLayer = useRef(null); // Keep a reference to the active tile layer 

  const { theme } = useTheme();
  const isMobileViewport = useWindowWidth() < 768;
  const [isSummarySheetOpen, setIsSummarySheetOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);

  const [isMapLoading, setIsMapLoading] = useState(false);

  const [countyData, setCountyData] = useState(null);
  const [polygonData, setPolygonData] = useState(null);
  const [stateTotals, setStateTotals] = useState(null); // US Map percentages
  const [countyTotals, setCountyTotals] = useState(null); // State Map percentages

  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setStateTotals(null);
      setCountyTotals(null);
      return;
    }

    fetch('/data/statetotal.json')
      .then(res => res.json())
      .then(data => setStateTotals(data))
      .catch(err => console.error("Failed to fetch state totals for map:", err));

    fetch('/data/countytotal.json')
      .then(res => res.json())
      .then(data => setCountyTotals(data))
      .catch(err => console.error("Failed to fetch county totals for map:", err));
  }, [isAuthenticated]);

  const [regionData, setRegionData] = useState(null);
  const [filteredPredictionData, setFilteredPredictionData] = useState({});
  const selectedDate = selectedDay;
  const [viewMode, setViewMode] = useState('percent'); // 'percent' or 'absolute'
  const [isDurationView, setIsDurationView] = useState(false); // Duration map mode toggle
  const [durationData, setDurationData] = useState(null); // Raw duration prediction data
  const [filteredDurationData, setFilteredDurationData] = useState({}); // Duration data filtered by selectedDate per county
  const effectiveViewMode = viewMode;
  const isUsView = !stateId || stateId === 'US';
  const isWeeklySummary = predictionType === 'weekly' && isUsView;
  const weeklyPeakKind = effectiveViewMode === 'percent' ? 'percentPeak' : 'absolutePeak';

  const weeklyCustomerPeaks = useMemo(() => {
    if (!isWeeklySummary) return {};
    return buildWeeklyCustomerPeaks(predictionData || {}, stateTotals || {});
  }, [isWeeklySummary, predictionData, stateTotals]);

  const selectedWeeklyStateId = isWeeklySummary
    && currentRegion?.type === 'STATE'
    ? String(currentRegion.id).padStart(2, '0')
    : null;

  const weeklySummary = useMemo(() => {
    if (!isWeeklySummary) return null;

    const records = Object.keys(weeklyCustomerPeaks)
      .map((stateFips) => {
        const peak = getWeeklyCustomerPeak(
          weeklyCustomerPeaks,
          stateFips,
          currentMetric,
          weeklyPeakKind
        );
        if (!peak) return null;

        const value = Number(peak.value);
        const percentOut = peak.percentOut === null || peak.percentOut === undefined
          ? null
          : Number(peak.percentOut);

        return {
          id: stateFips,
          name: FIPS_TO_NAME[stateFips] || FIPS_TO_ABBR[stateFips] || `State ${stateFips}`,
          value: Number.isFinite(value) ? value : 0,
          percentOut: Number.isFinite(percentOut) ? percentOut : null,
          date: peak.date
        };
      })
      .filter(Boolean);

    const rankedRecords = [...records].sort((left, right) => {
      if (effectiveViewMode === 'percent') {
        const percentDifference = (right.percentOut ?? -1) - (left.percentOut ?? -1);
        if (percentDifference !== 0) return percentDifference;
      }
      if (right.value !== left.value) return right.value - left.value;
      return left.id.localeCompare(right.id);
    });

    const topStates = getTopWeeklyCustomerStates(
      weeklyCustomerPeaks,
      currentMetric
    ).map((record) => ({
      ...record,
      name: FIPS_TO_NAME[record.id] || FIPS_TO_ABBR[record.id] || `State ${record.id}`
    }));

    return {
      metricLabel: `${currentMetric} est.`,
      leader: rankedRecords[0] || null,
      selectedRecord: selectedWeeklyStateId
        ? records.find((record) => record.id === selectedWeeklyStateId) || null
        : null,
      topStates
    };
  }, [
    isWeeklySummary,
    weeklyCustomerPeaks,
    currentMetric,
    weeklyPeakKind,
    selectedWeeklyStateId,
    effectiveViewMode
  ]);

  // Duration bucket color mapping (used by styleFeature, legend, and labels)
  const DURATION_BUCKET_COLORS = {
    up_to_12h: '#22c55e',  // Green
    h12_24: '#f59e0b',     // Amber
    h24_72: '#f87171',     // Light red
    gt_72h: '#dc2626'      // Red
  };
  const DURATION_BUCKET_LABELS = {
    up_to_12h: '0\u201312h',
    h12_24: '12\u201324h',
    h24_72: '24\u201372h',
    gt_72h: '> 72h'
  };
  const DURATION_SHORT_LABELS = {
    up_to_12h: '<12',
    h12_24: '12-24',
    h24_72: '24-72',
    gt_72h: '72>'
  };
  const [selectedDurationBucket, setSelectedDurationBucket] = useState(null);
  const [metricsData, setMetricsData] = useState({
    customers: {
      min: {},
      mean: {},
      max: {},
      weather_severity: {}
    },
    outages: {
      min: {},
      mean: {},
      max: {},
      weather_severity: {}
    }
  });


  useEffect(() => {
    if (currentRegion) {
      setIsSummarySheetOpen(true);
    }
  }, [currentRegion]);

  useEffect(() => {
    setIsSummarySheetOpen(false);
  }, [stateId]);


  function processMetricsData(predictionData) {
    const newMetricsData = {
      customers: {
        min: {},
        mean: {},
        max: {},
        weather_severity: {}
      },
      outages: {
        min: {},
        mean: {},
        max: {},
        weather_severity: {}
      }
    };

    Object.entries(predictionData).forEach(([regionId, data]) => {
      if (data.customers) {
        newMetricsData.customers.mean[regionId] = data.customers.mean;
        newMetricsData.customers.min[regionId] = Math.round(data.customers.min);
        newMetricsData.customers.max[regionId] = Math.round(data.customers.max);
        newMetricsData.customers.weather_severity[regionId] = data.customers.weather_severity;
      } else {
        newMetricsData.customers.mean[regionId] = 0;
        newMetricsData.customers.min[regionId] = 0;
        newMetricsData.customers.max[regionId] = 0;
        newMetricsData.customers.weather_severity[regionId] = 0;
      }

      if (data.outage) {
        newMetricsData.outages.mean[regionId] = data.outage.mean;
        newMetricsData.outages.min[regionId] = Math.round(data.outage.min);
        newMetricsData.outages.max[regionId] = Math.round(data.outage.max);
        newMetricsData.outages.weather_severity[regionId] = data.outage.weather_severity;
      } else {
        newMetricsData.outages.mean[regionId] = 0;
        newMetricsData.outages.min[regionId] = 0;
        newMetricsData.outages.max[regionId] = 0;
        newMetricsData.outages.weather_severity[regionId] = 0;
      }
    });

    return newMetricsData;
  }

  // min mean max tooltip
  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach(el => {
      // Dispose existing instance (important for React re-renders)
      if (bootstrap.Tooltip.getInstance(el)) {
        bootstrap.Tooltip.getInstance(el).dispose();
      }
      new bootstrap.Tooltip(el);
    });
  }, [isDurationView]);

  // Duration label mapping for argmax display
  const DURATION_LABELS = {
    up_to_12h: '0–12 hours',
    h12_24: '12–24 hours',
    h24_72: '24–72 hours',
    gt_72h: '> 72 hours'
  };

  // Compute most likely duration bucket from a duration probabilities object
  const getMostLikelyDuration = (probs) => {
    if (!probs) return null;
    let maxKey = null;
    let maxVal = -1;
    for (const [key, val] of Object.entries(probs)) {
      if (val > maxVal) {
        maxVal = val;
        maxKey = key;
      }
    }
    return maxKey ? DURATION_LABELS[maxKey] || maxKey : null;
  };


  useEffect(() => {
    // Reset selection on state change
    setRegionData(null);
    if (setCurrentRegion) setCurrentRegion(null);
    setDurationData(null);
    setFilteredDurationData({});

    let isMounted = true;
    const loadInitialGeoJson = async () => {
      if (!isMounted) return;
      setIsMapLoading(true); // Start loading indicator

      try {
        // Load state-level GeoJSON
        const geoData = await apiClient.getGeoJson(stateId); // Use updated stateId
        setCountyData(geoData);

        // Calculate and store initial bounds
        if (mapInstance.current && geoData) {
          // Hardcoded bounds for Alaska (FIPS code '02' or '2')
          if (stateId === '02' || stateId === '2' || stateId === 'AK') {
            initialPolygonBounds.current = L.latLngBounds(
              [51.2, -179.1],  // Southwest corner (south, west)
              [71.5, -129.9]   // Northeast corner (north, east)
            );
          } else {
            const tempLayer = L.geoJSON(geoData);
            initialPolygonBounds.current = tempLayer.getBounds();
          }
        }

      } catch (error) {
        console.error('Error loading initial map data:', error);
      } finally {
        if (isMounted) setIsMapLoading(false);
      }
    };

    if (stateId && stateId !== 'US') {
      loadInitialGeoJson();
    }

    return () => { isMounted = false; };

  }, [stateId]); // Rerun when stateId changes

  // Fetch duration prediction data when stateId changes (only for state-level view, not US)
  // NOTE: Duration view is ADDED BACK
  useEffect(() => {
    if (!stateId || stateId === 'US') {
      setIsDurationView(false);
      setSelectedDurationBucket(null);
    }
    if (!stateId || stateId === 'US' || !statesList || statesList.length === 0) {
      return;
    }

    let isMounted = true;
    const fetchDuration = async () => {
      try {
        // Resolve backend state ID from FIPS, same pattern as Home.jsx
        let backendId = stateId;
        const stateObj = statesList.find(s => parseInt(s.fips) === parseInt(stateId));
        if (stateObj) {
          backendId = stateObj.id;
        } else {
          console.warn('Duration: State not found for FIPS:', stateId);
        }

        const today = new Date().toISOString().split('T')[0];
        const nextWeek = new Date(Date.now() + 6 * 86400000).toISOString().split('T')[0];

        const data = await apiClient.getDurationPrediction(backendId, startDate || today, endDate || nextWeek, timezone);
        if (isMounted) {
          setDurationData(data);
        }
      } catch (err) {
        console.error('Failed to fetch duration prediction:', err);
      }
    };

    fetchDuration();
    return () => { isMounted = false; };
  }, [stateId, statesList, startDate, endDate, timezone]);

  // Filter duration data by selectedDate
  useEffect(() => {
    if (!durationData || !selectedDate) {
      setFilteredDurationData({});
      return;
    }

    const filtered = {};
    Object.entries(durationData).forEach(([countyFips, dataArray]) => {
      if (!Array.isArray(dataArray)) return;
      const match = dataArray.find(entry => entry.date && entry.date.startsWith(selectedDate));
      if (match) {
        filtered[countyFips] = match;
      }
    });

    setFilteredDurationData(filtered);
  }, [durationData, selectedDate]);


  useEffect(() => { // tooltip safety system
    if (!mapInstance.current) return;

    const map = mapInstance.current;

    const forceCloseAllTooltips = () => {
      map.eachLayer((l) => {
        if (l.closeTooltip) {
          try { l.closeTooltip(); } catch { }
        }
      });
      activeTooltipLayer.current = null;
    };

    map.getContainer().addEventListener("mouseleave", forceCloseAllTooltips);
    window.addEventListener("blur", forceCloseAllTooltips);

    return () => {
      map.getContainer().removeEventListener("mouseleave", forceCloseAllTooltips);
      window.removeEventListener("blur", forceCloseAllTooltips);
    };
  }, []);

  // Update metrics data when prediction data changes
  useEffect(() => {
    if (predictionData && selectedDate) {
      const filteredData = {};

      Object.entries(predictionData).forEach(([regionId, dataArray]) => {
        const match = dataArray.find(
          (entry) => entry.date.startsWith(selectedDate)
        );
        if (match) {
          filteredData[regionId] = {
            customers: match.customers,
            outage: match.outage
          };
        }
      });

      // Aggregation Logic for US View
      let dataToProcess = filteredData;

      if (!stateId || stateId === 'US') {
        const aggregated = {};

        Object.entries(filteredData).forEach(([countyFips, data]) => {
          // County FIPS is 5 digits, State FIPS is first 2
          const stateFips = countyFips.substring(0, 2);

          if (!aggregated[stateFips]) {
            aggregated[stateFips] = {
              customers: {
                mean: 0, min: 0, max: 0,
                mean_percent_out: 0, min_percent_out: 0, max_percent_out: 0,
                weather_severity: 0, count: 0
              },
              outage: {
                mean: 0, min: 0, max: 0,
                mean_percent_out: 0, min_percent_out: 0, max_percent_out: 0,
                weather_severity: 0, count: 0
              }
            };
          }

          // Sum up values
          // For severity, we might want max or average. Let's take max for severity.
          aggregated[stateFips].customers.mean += data.customers.mean;
          aggregated[stateFips].customers.min += data.customers.min;
          aggregated[stateFips].customers.max += data.customers.max;
          aggregated[stateFips].customers.mean_percent_out += data.customers.mean_percent_out || 0;
          aggregated[stateFips].customers.min_percent_out += data.customers.min_percent_out || 0;
          aggregated[stateFips].customers.max_percent_out += data.customers.max_percent_out || 0;
          aggregated[stateFips].customers.weather_severity = Math.max(aggregated[stateFips].customers.weather_severity, data.customers.weather_severity);

          aggregated[stateFips].outage.mean += data.outage.mean;
          aggregated[stateFips].outage.min += data.outage.min;
          aggregated[stateFips].outage.max += data.outage.max;
          aggregated[stateFips].outage.mean_percent_out += data.outage.mean_percent_out || 0;
          aggregated[stateFips].outage.min_percent_out += data.outage.min_percent_out || 0;
          aggregated[stateFips].outage.max_percent_out += data.outage.max_percent_out || 0;
          aggregated[stateFips].outage.weather_severity = Math.max(aggregated[stateFips].outage.weather_severity, data.outage.weather_severity);
        });

        dataToProcess = aggregated;
      }

      setFilteredPredictionData(dataToProcess);
      const newMetricsData = processMetricsData(dataToProcess);
      setMetricsData(newMetricsData);
    }
  }, [predictionData, selectedDate, stateId]);



  // Function to clear all existing labels
  const clearLabels = () => {
    if (labelMarkers.current) {
      labelMarkers.current.forEach(marker => {
        if (marker && mapInstance.current) {
          mapInstance.current.removeLayer(marker);
        }
      });
      labelMarkers.current = [];
    }
  };

  const createCenteredLabel = (layer, value, feature) => {
    const centroid = calculateCentroid(feature.geometry.coordinates, feature.geometry.type);
    if (!centroid) return null;

    const geoid = feature.properties.GEOID || feature.id;
    let displayValue = new Intl.NumberFormat().format(value);

    if (viewMode === 'percent' && countyTotals) {
      const total = countyTotals[geoid];
      if (total) {
        const pct = (value / total) * 100;
        displayValue = `${pct.toFixed(2)}%`;
      }
    }

    const label = L.divIcon({
      className: 'region-label-container',
      html: `<div class="region-label"><div class="region-value">${displayValue}</div></div>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0]
    });

    const marker = L.marker(centroid, {
      icon: label,
      interactive: false,
      zIndexOffset: 1000
    });

    labelMarkers.current.push(marker);
    return marker;
  };

  // if (mapInstance.current && countyData && polygonData) {

  // Update GeoJSON layer when metrics data or GeoJSON data changes
  useEffect(() => {
    if (isMapReady && mapInstance.current && countyData) {
      clearLabels(); // Clear existing labels before updating
      updateGeoJsonLayer();
    }
  }, [metricsData, currentMetric, currentDataType, countyData, polygonData, countiesList, viewMode, countyTotals, filteredDurationData, isDurationView, selectedDurationBucket, isMapReady, currentRegion]);

  /* New Loading State for Weather Summary */
  const [isWeatherSummaryLoading, setIsWeatherSummaryLoading] = useState(false);

  // Re-fetch weather summary when selectedDate changes while a county is already selected
  useEffect(() => {
    if (!currentRegion || !selectedDate || !timezone || !countiesList || countiesList.length === 0) return;
    if (!stateId || stateId === 'US') return;

    const countyObj = countiesList.find(c => String(c.fips) === String(currentRegion));
    if (!countyObj) return;
    const backendId = countyObj.id;

    let isMounted = true;
    const refetchWeather = async () => {
      setIsWeatherSummaryLoading(true);
      setRegionData(prev => prev ? { ...prev, weatherData: null } : prev);
      try {
        const weatherSummary = await apiClient.getWeatherSummary(selectedDate, timezone, backendId);
        if (isMounted) {
          setRegionData(prev => prev ? {
            ...prev,
            customers: filteredPredictionData[currentRegion]?.customers,
            outages: filteredPredictionData[currentRegion]?.outage,
            weatherData: weatherSummary
          } : prev);
        }
      } catch (err) {
        console.error('Failed to re-fetch weather summary on date change:', err);
      } finally {
        if (isMounted) setIsWeatherSummaryLoading(false);
      }
    };

    refetchWeather();
    return () => { isMounted = false; };
  }, [selectedDate, currentRegion]);

  // Dedicated Click Handler Function
  const handleFeatureClick = async (e, feature, geoid) => {
    const metricValues = metricsData[currentDataType][currentMetric];
    const hasData = metricValues && metricValues.hasOwnProperty(geoid);
    const value = hasData ? metricValues[geoid] : 0;

    // If we are at US level (stateId assumed 'US' or null), clicking drills down
    if (!stateId || stateId === 'US') {
      onCountyClick({ id: geoid, name: feature.properties.NAME, type: 'STATE' });
      return;
    }

    // We are at State level, clicking selects a county for details
    const clickedCounty = {
      id: geoid,
      name: feature.properties.NAME || feature.properties.name || 'Unknown County',
      value: value,
      coordinates: e.latlng
    };

    setCurrentRegion(geoid);
    onCountyClick(clickedCounty);

    try {
      // Robust Backend ID Lookup
      let backendId = null;
      if (countiesList && countiesList.length > 0) {
        // Robust check: convert both to strings to ensure matching "01001" vs 1001
        const countyObj = countiesList.find(c => String(c.fips) === String(geoid));

        if (countyObj) {
          backendId = countyObj.id;
        } else {
          console.warn(`County with FIPS ${geoid} not found in countiesList of size ${countiesList.length}.`);
        }
      } else {
        console.warn("countiesList is empty or undefined when clicking. weather/summary cannot be called.");
      }

      if (backendId) {
        if (!selectedDate || !timezone) {
          console.error("Missing date or timezone for weather summary fetch.", { selectedDate, timezone });
        } else {
          setIsWeatherSummaryLoading(true); // START LOADING
          setRegionData(prev => ({
            ...prev,
            ...clickedCounty, // optimistic update
            weatherData: null, // clear old weather data while loading
            customers: filteredPredictionData[geoid]?.customers,
            outages: filteredPredictionData[geoid]?.outage
          }));

          try {
            const weatherSummary = await apiClient.getWeatherSummary(selectedDate, timezone, backendId);
            const countyDetails = countiesList?.find(c => c.id === backendId);

            setRegionData(prev => ({
              ...prev,
              ...countyDetails,
              customers: filteredPredictionData[geoid]?.customers,
              outages: filteredPredictionData[geoid]?.outage,
              countyData: clickedCounty,
              weatherData: weatherSummary
            }));
          } catch (innerErr) {
            console.error("Inner weather fetch error", innerErr);
          } finally {
            setIsWeatherSummaryLoading(false); // END LOADING
          }
        }
      } else {
        // If we can't find backend ID, we still might want to show what we have
        console.error(`Backend ID not found for FIPS ${geoid}. Aborting weather fetch.`);

        // Update regionData anyway so other-custom shows up?
        setRegionData({
          customers: filteredPredictionData[geoid]?.customers,
          outages: filteredPredictionData[geoid]?.outage,
          countyData: clickedCounty,
          weatherData: null // Explicitly null
        });
      }
    } catch (err) {
      console.error("Failed to fetch weather summary:", err);
    }
  };

  // Function to update GeoJSON layer with current data
  const updateGeoJsonLayer = () => {
    if (geoJsonLayer.current) {
      mapInstance.current.removeLayer(geoJsonLayer.current);
    }

    // Filter features based on available data
    const filteredCountyData = {
      ...countyData,
      features: countyData.features.filter(feature => {
        const geoid = feature.properties.GEOID || feature.id;
        // In duration view, include counties that have duration data
        if (isDurationView) {
          return filteredDurationData && filteredDurationData[geoid] && filteredDurationData[geoid][currentMetric];
        }
        if (!metricsData || !metricsData[currentDataType] || !metricsData[currentDataType][currentMetric]) return false;
        const value = metricsData[currentDataType][currentMetric][geoid];
        return value !== undefined; // Include features with zero values
      })
    };

    // Check if we have any features to display
    if (filteredCountyData.features.length === 0) {
      console.warn('No features to display for current data type and metric');
      return;
    }

    if (geoJsonLayer.current) {
      geoJsonLayer.current.eachLayer(l => l.closeTooltip && l.closeTooltip());
      mapInstance.current.removeLayer(geoJsonLayer.current);
    }

    geoJsonLayer.current = L.geoJSON(filteredCountyData, {
      style: (feature) => {
        const geoid = feature.properties.GEOID || feature.id;
        return styleFeature({ ...feature, properties: { ...feature.properties, GEOID: geoid } });
      },
      onEachFeature: (feature, layer) => {
        const geoid = feature.properties.GEOID || feature.id;
        layer.on({
          click: (e) => {
            const layer = e.target;

            if (activeTooltipLayer.current) {
              activeTooltipLayer.current.closeTooltip();
              activeTooltipLayer.current = null;
            }

            activeTooltipLayer.current = layer;
            layer.openTooltip();
            layer.setStyle({ weight: 2, fillOpacity: 0.9 });
            layer.bringToFront();
            handleFeatureClick(e, feature, geoid);
          },
          mouseover: (e) => {
            const layer = e.target;
            // close previously opened tooltip (prevents stacking across borders)
            if (activeTooltipLayer.current && activeTooltipLayer.current !== layer) {
              activeTooltipLayer.current.closeTooltip();
            }
            activeTooltipLayer.current = layer;
            layer.openTooltip();
            layer.setStyle({ weight: 2, fillOpacity: 0.9 });
            layer.bringToFront();
          },
          mouseout: (e) => {
            const layer = e.target;
            if (activeTooltipLayer.current === layer) {
              activeTooltipLayer.current = null;
            }
            layer.closeTooltip();
            if (isDurationView) {
              const hasDur = filteredDurationData && filteredDurationData[geoid];
              layer.setStyle({ weight: 0.5, fillOpacity: hasDur ? 0.6 : 0.3 });
            } else {
              const metricValues = metricsData[currentDataType][currentMetric];
              const hasData = metricValues && metricValues.hasOwnProperty(geoid);
              if (!hasData) {
                layer.setStyle({ weight: 0.5, fillOpacity: 0.3 });
              } else {
                layer.setStyle({ weight: 0.5, fillOpacity: 0.6 });
              }
            }
          }
        });

        // --- Base Duration View: tooltip and label ---
        if (isDurationView) {
          const durationEntry = filteredDurationData[geoid];
          const durationProbs = durationEntry ? durationEntry[currentMetric] : null;
          if (durationProbs) {
            const mostLikelyKey = getMostLikelyDuration(durationProbs);
            // Find raw key for color
            let argmaxKey = null;
            let argmaxVal = -1;
            for (const [k, v] of Object.entries(durationProbs)) {
              if (v > argmaxVal) { argmaxVal = v; argmaxKey = k; }
            }

            // Build tooltip showing all buckets
            const bucketRows = Object.entries(DURATION_BUCKET_LABELS).map(([key, label]) => {
              const prob = durationProbs[key];
              const pct = prob != null ? (prob * 100).toFixed(1) : '0.0';
              const isHighlight = selectedDurationBucket ? (key === selectedDurationBucket) : (key === argmaxKey);
              const bgStyle = isHighlight ? `background: ${DURATION_BUCKET_COLORS[key]}; color: #fff; font-weight: bold; padding: 1px 5px; border-radius: 8px;` : '';
              return `<div style="display:flex; justify-content:space-between; font-size:12px; padding:1px 2px;">
                <span>${label}</span>
                <span style="${bgStyle}">${pct}%</span>
              </div>`;
            }).join('');

            layer.bindTooltip(`
              <div style="background-color: var(--bg-dropdown); color: var(--text-main); padding: 4px 6px; border: 1px solid var(--dropdown-border); border-radius: 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.3); min-width: 140px;">
                <div style="font-weight: bold; padding:2px; font-size: 14px; border-bottom: 1px solid var(--dropdown-border); margin-bottom: 3px;">${feature.properties.NAME} County</div>
                <div style="font-size: 11px; color: var(--text-muted); margin-bottom: 2px;">Outage Duration (${currentMetric})</div>
                ${bucketRows}
              </div>
            `, {
              direction: 'top',
              interactive: false,
              permanent: false,
              className: 'custom-dark-tooltip',
              opacity: 1
            });
          }
        } else {
          // --- Normal (outage prediction) view ---
          const metricValues = metricsData[currentDataType][currentMetric];
          const hasData = metricValues && metricValues.hasOwnProperty(geoid);
          const value = hasData ? metricValues[geoid] : 0;

          let displayValue = value.toLocaleString();
          if (hasData && viewMode === 'percent' && countyTotals) {
            const total = countyTotals[geoid];
            if (total) {
              const pct = (value / total) * 100;
              displayValue = `${value.toLocaleString()} (${pct.toFixed(2)}%)`;
            }
          }

          if (hasData) {
            // Compute duration "Most Likely" line for tooltip
            const durationEntry = filteredDurationData[geoid];
            const durationProbs = durationEntry ? durationEntry[currentMetric] : null;
            const mostLikelyDuration = getMostLikelyDuration(durationProbs);
            const durationLine = mostLikelyDuration
              ? `<div style="font-size: 12px; margin-top: 3px; padding: 2px;">
                   <span style="color: var(--text-muted);">Most Likely Duration:</span>
                   <span style="color: #fff; font-weight: bold; margin-left: 4px; background: #7c3aed; padding: 2px 6px; border-radius: 10px; font-size: 12px;">${mostLikelyDuration}</span>
                 </div>`
              : '';

            layer.bindTooltip(`
              <div style="background-color: var(--bg-dropdown); color: var(--text-main); padding: 1px; border: 1px solid var(--dropdown-border); border-radius: 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">
                <div style="font-weight: bold; padding:2px; font-size: 14px;">${feature.properties.NAME} County</div>
                <div style="font-size: 13px;">
                   <span style="color: var(--text-muted); padding:2px; ">${currentDataType === 'customers' ? 'Customers Out' : 'Outages'}:</span> 
                   <span style="color: #fff; font-weight: bold; margin-left: 4px; background: var(--blue-primary); padding: 2px 6px; border-radius: 10px; font-size: 14px;">${displayValue}</span>
                </div>
                ${durationLine}
              </div>
            `, {
              direction: 'top',
              interactive: false,
              permanent: false,
              className: 'custom-dark-tooltip',
              opacity: 1
            });
          }
        }
      }
    }).addTo(mapInstance.current);

    // Update polygon layer
    if (polygonLayer.current) {
      mapInstance.current.removeLayer(polygonLayer.current);
    }

    polygonLayer.current = L.geoJSON(polygonData, {
      style: {
        color: '#3366ff',
        weight: 3,
        opacity: 0.8,
        fillColor: 'transparent', // Ensure transparent fill
        dashArray: '5, 10',
        lineCap: 'round',
        pointerEvents: 'none', // Add explicit pointer events override for style if supported, otherwise interactive: false handles it
      },
      interactive: false, // Prevent this layer from capturing clicks
      pane: 'overlayPane'
    }).addTo(mapInstance.current);

    // Ensure proper layer order
    if (geoJsonLayer.current) {
      polygonLayer.current.bringToFront();
      geoJsonLayer.current.bringToFront();
    }

    try {
      let boundsToUse = null;

      // Hardcoded bounds for Alaska (FIPS code '02' or '2')
      // Alaska bounds: Southwest [51.2, -179.1] to Northeast [71.5, -129.9]
      if (stateId === '02' || stateId === '2' || stateId === 'AK') {
        boundsToUse = L.latLngBounds(
          [51.2, -179.1],  // Southwest corner (south, west)
          [71.5, -129.9]   // Northeast corner (north, east)
        );
      } else if (initialPolygonBounds.current && initialPolygonBounds.current.isValid()) {
        boundsToUse = initialPolygonBounds.current;
      } else {
        const bounds = geoJsonLayer.current.getBounds();
        if (bounds.isValid()) {
          boundsToUse = bounds;
        } else {
          console.warn('Invalid bounds, using default view');
          mapInstance.current.setView([40.0, -89.0], 7);
          return;
        }
      }

      // Set view and bounds
      // Only fit bounds if we haven't fitted for this state yet (or if enforced)
      if (lastFittedStateId.current !== stateId) {
        mapInstance.current.fitBounds(boundsToUse);
        lastFittedStateId.current = stateId;
      }

      mapInstance.current.setMaxBounds(boundsToUse.pad(0.1));

      // Optional: Dynamically set minZoom to prevent zooming out of bounds
      const minZoom = mapInstance.current.getBoundsZoom(boundsToUse, false);
      mapInstance.current.setMinZoom(minZoom);

    } catch (error) {
      console.warn('Error setting map bounds:', error);
      mapInstance.current.setView([38, -97], 4);
    }
  };


  // --- Legend Implementation ---
  useEffect(() => {
    if (!mapInstance.current) return;
    // For normal mode, check metricsData exists
    if (!isDurationView && (!metricsData || !metricsData[currentDataType] || !metricsData[currentDataType][currentMetric])) {
      return;
    }

    const map = mapInstance.current;

    // Create Legend Logic
    const legend = L.control({ position: 'bottomleft' });

    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'info legend');

      div.style.backgroundColor = theme === 'dark' ? 'rgba(30, 30, 30, 0.8)' : 'rgba(255, 255, 255, 0.9)';
      div.style.padding = '10px';
      div.style.borderRadius = '5px';
      div.style.boxShadow = '0 0 15px rgba(0,0,0,0.2)';
      div.style.color = theme === 'dark' ? '#fff' : '#111';
      div.style.fontSize = '14px';
      div.style.lineHeight = '18px';

      let labels = [];

      if (isDurationView) {
        if (selectedDurationBucket) {
          // Heatmap legend for specific probability
          let colors = [
            '#22c55e', '#84cc16', '#a3e635', '#fbbf24', '#f59e0b',
            '#fb923c', '#f87171', '#ef4444', '#dc2626', '#991b1b'
          ];
          if (selectedDurationBucket === 'up_to_12h') {
            colors = colors.slice().reverse();
          }
          labels.push(`<strong style="display:block; margin-bottom:5px; border-bottom:1px solid #555; padding-bottom:3px;">Probability (${DURATION_SHORT_LABELS[selectedDurationBucket]}h)</strong>`);
          let gradient = 'background: linear-gradient(to right';
          colors.forEach((c) => { gradient += `, ${c}`; });
          gradient += ');';
          labels.push(`
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;">
               <span>0%</span>
               <span>100%</span>
            </div>
            <div style="width: 100%; height: 10px; ${gradient} border-radius: 2px;"></div>
          `);
        } else {
          // Duration legend: show 4 colored swatches
          labels.push(`<strong style="display:block; margin-bottom:5px; border-bottom:1px solid #555; padding-bottom:3px;">Outage Duration</strong>`);
          const buckets = [
            { key: 'up_to_12h', label: '0\u201312 hours' },
            { key: 'h12_24', label: '12\u201324 hours' },
            { key: 'h24_72', label: '24\u201372 hours' },
            { key: 'gt_72h', label: '> 72 hours' }
          ];
          buckets.forEach(b => {
            labels.push(`<div style="display:flex; align-items:center; margin-bottom:3px;">
              <span style="display:inline-block; width:16px; height:12px; background:${DURATION_BUCKET_COLORS[b.key]}; border-radius:2px; margin-right:6px; flex-shrink:0;"></span>
              <span style="font-size:12px;">${b.label}</span>
            </div>`);
          });
        }
      } else {
        // Normal outage legend
        let min = 0, max = 100;
        if (viewMode === 'percent') {
          min = 0;
          max = 40;
        } else {
          const allValues = Object.values(metricsData[currentDataType][currentMetric])
            .filter(val => val !== undefined && val > 0);
          min = allValues.length > 0 ? Math.min(...allValues) : 0;
          max = allValues.length > 0 ? Math.max(...allValues) : 100;
        }

        const colors = [
          '#22c55e', '#84cc16', '#a3e635', '#fbbf24', '#f59e0b',
          '#fb923c', '#f87171', '#ef4444', '#dc2626', '#991b1b'
        ];

        const title = currentDataType === 'customers' ? 'Customers Out' : 'Outages';
        labels.push(`<strong style="display:block; margin-bottom:5px; border-bottom:1px solid #555; padding-bottom:3px;">${title} (${currentMetric})</strong>`);

        let gradient = 'background: linear-gradient(to right';
        colors.forEach((c) => { gradient += `, ${c}`; });
        gradient += ');';

        labels.push(`
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;">
             <span>${min.toLocaleString()}${viewMode === 'percent' ? '%' : ''}</span>
             <span>${max.toLocaleString()}${viewMode === 'percent' ? '%' : ''}${viewMode === 'percent' ? '+' : ''}</span>
          </div>
          <div style="width: 100%; height: 10px; ${gradient} border-radius: 2px;"></div>
        `);
      }

      div.innerHTML = labels.join('');
      return div;
    };

    legend.addTo(map);

    // Cleanup function to remove the control when dependencies change or unmount
    return () => {
      if (map && legend) {
        legend.remove();
      }
    };

  }, [metricsData, currentDataType, currentMetric, stateId, viewMode, theme, isDurationView, selectedDurationBucket]);


  // Removed redundant updateWeatherSummary useEffect to prevent state mutation and double fetching.

  // Initialize map
  useEffect(() => {
    // Only initialize Leaflet map if we are NOT in US view (Plotly view)
    if ((!stateId || stateId === 'US')) {
      return;
    }

    if (!mapInstance.current && mapRef.current) {
      mapInstance.current = L.map(mapRef.current, {
        center: [40.0, -89.0],
        zoom: 7,
        zoomControl: false,
        attributionControl: false
      });

      // Add attribution and zoom controls
      const attribution = L.control.attribution();
      attribution.setPrefix('');
      attribution.addTo(mapInstance.current);

      L.control.zoom({
        position: 'topright'
      }).addTo(mapInstance.current);

      // Add Carto basemap based on theme
      const tileUrl = theme === 'dark'
        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_2l38_1_67edb4a0fb30722954efd14d'
        : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2l38_1_67edb4a0fb30722954efd14d';

      currentTileLayer.current = L.tileLayer(tileUrl, {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
        crossOrigin: true
      }).addTo(mapInstance.current);

      setIsMapReady(true);

      // If polygon data is already loaded, calculate initial bounds
      if (polygonData) {
        // Hardcoded bounds for Alaska (FIPS code '02' or '2')
        if (stateId === '02' || stateId === '2' || stateId === 'AK') {
          initialPolygonBounds.current = L.latLngBounds(
            [51.2, -179.1],  // Southwest corner (south, west)
            [71.5, -129.9]   // Northeast corner (north, east)
          );
        } else {
          const tempLayer = L.geoJSON(polygonData);
          initialPolygonBounds.current = tempLayer.getBounds();
        }
      }
    }

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        lastFittedStateId.current = null;
        setIsMapReady(false);
      }
    };
  }, [stateId]);

  // Update basemap when theme changes
  const TILE_LAYERS = {
    dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_2l38_1_67edb4a0fb30722954efd14d',
    light: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2l38_1_67edb4a0fb30722954efd14d'
  };

  useEffect(() => {
    if (mapInstance.current && currentTileLayer.current) {
      currentTileLayer.current.setUrl(TILE_LAYERS[theme]);
    }
  }, [theme]);


  useEffect(() => {
    if (mapInstance.current && polygonData && !initialPolygonBounds.current) {
      // Hardcoded bounds for Alaska (FIPS code '02' or '2')
      if (stateId === '02' || stateId === '2' || stateId === 'AK') {
        initialPolygonBounds.current = L.latLngBounds(
          [51.2, -179.1],  // Southwest corner (south, west)
          [71.5, -129.9]   // Northeast corner (north, east)
        );
      } else {
        const tempLayer = L.geoJSON(polygonData);
        initialPolygonBounds.current = tempLayer.getBounds();
      }
    }
  }, [polygonData, stateId]);


  // Helper functions
  const calculateCentroid = (coordinates, type = 'Polygon') => {
    if (!coordinates || coordinates.length === 0) return null;

    let points;
    if (type === 'MultiPolygon') {
      // Find the polygon with the largest coordinates array (most complex)
      let maxLen = -1;
      let targetPolygon = coordinates[0];
      coordinates.forEach(poly => {
        if (poly[0] && poly[0].length > maxLen) {
          maxLen = poly[0].length;
          targetPolygon = poly;
        }
      });
      points = targetPolygon[0];
    } else {
      points = coordinates[0];
    }

    if (!points || points.length < 3) return null;

    let area = 0;
    let cx = 0;
    let cy = 0;

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const factor = (p1[0] * p2[1]) - (p2[0] * p1[1]);
      area += factor;
      cx += (p1[0] + p2[0]) * factor;
      cy += (p1[1] + p2[1]) * factor;
    }

    area = area / 2;
    if (Math.abs(area) < 1e-10) return null; // Prevent division by zero for degenerate polygons

    const f = area * 6;
    const res = [cy / f, cx / f];
    if (isNaN(res[0]) || isNaN(res[1])) return null;
    return res;
  };

  const getColor = (value, isPercent = false) => {
    let normalizedValue;

    if (isPercent) {
      // Calculate normalized value based on 0 to 40% range
      normalizedValue = Math.min(value / 40, 1);
    } else {
      // Get all non-zero values for the current metric and data type
      const allValues = Object.values(metricsData[currentDataType][currentMetric])
        .filter(val => val !== undefined && val > 0);

      // Calculate min and max, or use fallbacks if no data
      const min = allValues.length > 0 ? Math.min(...allValues) : 0;
      const max = allValues.length > 0 ? Math.max(...allValues) : 100;

      // Calculate where the current value falls in the range from 0 to 1
      // Handle case where min=max (prevent division by zero)
      normalizedValue = (max > min) ?
        (value - min) / (max - min) :
        (value > 0 ? 1 : 0);
    }

    // Extended color scale with more variations from green (low) to red (high)
    if (normalizedValue > 0.9) {
      return '#991b1b'; // Deeper red (highest)
    } else if (normalizedValue > 0.8) {
      return '#dc2626'; // Bright red (very high)
    } else if (normalizedValue > 0.7) {
      return '#ef4444'; // Regular red (high)
    } else if (normalizedValue > 0.6) {
      return '#f87171'; // Light red (moderately high)
    } else if (normalizedValue > 0.5) {
      return '#fb923c'; // Orange (medium-high)
    } else if (normalizedValue > 0.4) {
      return '#f59e0b'; // Amber/yellow (medium)
    } else if (normalizedValue > 0.3) {
      return '#fbbf24'; // Yellow (medium-low)
    } else if (normalizedValue > 0.2) {
      return '#a3e635'; // Light yellow-green (low-medium)
    } else if (normalizedValue > 0.1) {
      return '#84cc16'; // Light green (low)
    } else if (normalizedValue > 0.05) {
      return '#65a30d'; // Medium green (very low)
    } else {
      return '#22c55e'; // Bright green (lowest)
    }
  };

  const styleFeature = (feature) => {
    const geoid = feature.properties.GEOID || feature.id;
    const selectedRegionId = typeof currentRegion === 'string' ? currentRegion : (currentRegion?.id ?? null);
    const isSelected = selectedRegionId && String(selectedRegionId) === String(geoid);

    // --- Duration view styling ---
    if (isDurationView) {
      const durationEntry = filteredDurationData[geoid];
      const durationProbs = durationEntry ? durationEntry[currentMetric] : null;
      if (!durationProbs) {
        return {
          color: theme === 'dark' ? '#666666' : '#cccccc',
          weight: 0.5, opacity: 0.5,
          fillColor: theme === 'dark' ? '#3f3f46' : '#e2e8f0',
          fillOpacity: 0.3, smoothFactor: 0.5, interactive: true
        };
      }
      if (selectedDurationBucket) {
        // Heatmap for the selected probability
        const prob = durationProbs[selectedDurationBucket] || 0;
        let normalizedValue = prob;

        if (selectedDurationBucket === 'up_to_12h') {
          // For <12 hours, high probability is a safe state. 
          // An exponent pushes values >50% firmly into the green spectrum, solving the "60% still looks yellow" visual bug.
          normalizedValue = Math.pow(1 - prob, 1.5);
        }
        let pColor;
        if (normalizedValue > 0.9) pColor = '#991b1b';
        else if (normalizedValue > 0.8) pColor = '#dc2626';
        else if (normalizedValue > 0.7) pColor = '#ef4444';
        else if (normalizedValue > 0.6) pColor = '#f87171';
        else if (normalizedValue > 0.5) pColor = '#fb923c';
        else if (normalizedValue > 0.4) pColor = '#f59e0b';
        else if (normalizedValue > 0.3) pColor = '#fbbf24';
        else if (normalizedValue > 0.2) pColor = '#a3e635';
        else if (normalizedValue > 0.1) pColor = '#84cc16';
        else if (normalizedValue > 0.05) pColor = '#65a30d';
        else pColor = '#22c55e';

        return {
          color: theme === 'dark' ? '#aaaaaa' : '#696666ff',
          weight: 0.5,
          opacity: theme === 'dark' ? 0.6 : 1,
          fillColor: pColor,
          fillOpacity: theme === 'dark' ? 0.6 : 0.8,
          smoothFactor: 0.5
        };
      } else {
        // Find argmax bucket
        let argmaxKey = null;
        let argmaxVal = -1;
        for (const [k, v] of Object.entries(durationProbs)) {
          if (v > argmaxVal) { argmaxVal = v; argmaxKey = k; }
        }
        return {
          color: theme === 'dark' ? '#aaaaaa' : '#696666ff',
          weight: 0.5,
          opacity: theme === 'dark' ? 0.6 : 1,
          fillColor: DURATION_BUCKET_COLORS[argmaxKey] || '#888',
          fillOpacity: theme === 'dark' ? 0.6 : 0.8,
          smoothFactor: 0.5
        };
      }
    }

    // --- Normal outage view styling ---
    const metricValues = metricsData[currentDataType][currentMetric];
    const hasData = metricValues && metricValues.hasOwnProperty(geoid);
    let value = hasData ? metricValues[geoid] : 0;

    let isPercent = false;
    if (hasData && viewMode === 'percent' && countyTotals) {
      const total = countyTotals[geoid];
      if (total) {
        value = (value / total) * 100;
        isPercent = true;
      }
    }

    if (!hasData) {
      return {
        color: theme === 'dark' ? '#666666' : '#cccccc',
        weight: 0.5, opacity: 0.5,
        fillColor: theme === 'dark' ? '#3f3f46' : '#e2e8f0',
        fillOpacity: 0.3, smoothFactor: 0.5, interactive: true
      };
    }

    return {
      color: isSelected ? '#197ccf' : (theme === 'dark' ? '#aaaaaa' : '#696666ff'),
      weight: isSelected ? 2.5 : 0.5,
      opacity: isSelected ? 1 : (theme === 'dark' ? 0.6 : 1),
      fillColor: getColor(value, isPercent),
      fillOpacity: isSelected ? 0.9 : (theme === 'dark' ? 0.6 : 0.8),
      smoothFactor: 0.5
    };
  };



  /* Plotly Data Preparation */
  const getPlotlyData = () => {
    if (!metricsData || !metricsData[currentDataType] || !metricsData[currentDataType][currentMetric]) return { locations: [], z: [], text: [] };
    const currentData = metricsData[currentDataType][currentMetric];
    const locations = [];
    const z = [];
    const text = [];

    // If we are in US view, metricsData keys are State FIPS ('01', etc.)
    // We need to convert to State Abbr ('AL') for Plotly
    Object.entries(currentData).forEach(([fips, val]) => {
      // Ensure fips is 2 chars string
      let fipsStr = fips.toString().padStart(2, '0');
      const abbr = FIPS_TO_ABBR[fipsStr];
      const name = FIPS_TO_NAME[fipsStr] || abbr;

      if (abbr) {
        let plotVal = val;
        let hoverText = val.toLocaleString();
        const backendDataTypeKey = currentDataType === 'customers' ? 'customers' : 'outage';
        const percentOutKey = `${currentMetric}_percent_out`;
        const backendPercentOut = filteredPredictionData?.[fipsStr]?.[backendDataTypeKey]?.[percentOutKey];

        const total = stateTotals ? (stateTotals[fipsStr] || stateTotals[parseInt(fipsStr)]) : null;
        const computedPercentOut = total ? ((val / total) * 100) : 0;
        // For signed-in users on US view, always compute percentage from state totals.
        // Keep guest behavior unchanged by still honoring backend percent when available.
        const percentOut = (!isAuthenticated && typeof backendPercentOut === 'number')
          ? backendPercentOut
          : computedPercentOut;
        const pctStr = ` (${percentOut.toFixed(2)}%)`;

        if (effectiveViewMode === 'percent') {
          plotVal = percentOut;
          hoverText = `${val.toLocaleString()}${pctStr}`;
        } else {
          // Absolute Mode - Map to discrete index 0 to 5 for equidistant colorbar
          if (val < 10000) plotVal = 0.5;
          else if (val < 50000) plotVal = 1.5;
          else if (val < 100000) plotVal = 2.5;
          else if (val < 500000) plotVal = 3.5;
          else plotVal = 4.5;
          hoverText = `${val.toLocaleString()}${pctStr}`;
        }

        locations.push(abbr);
        z.push(plotVal);
        // Guests see only state name; authenticated users see full figures
        if (isAuthenticated) {
          text.push(`${name}<br>${currentDataType === 'customers' ? 'Customers Out' : 'Outages'}: ${hoverText}`);
        } else {
          text.push(`${name}<br><i>Sign in to view figures</i>`);
        }
      }
    });
    return { locations, z, text };
  };

  const getWeeklyPlotlyData = () => {
    const locations = [];
    const z = [];
    const text = [];

    Object.keys(weeklyCustomerPeaks).forEach((stateFips) => {
      const peak = getWeeklyCustomerPeak(
        weeklyCustomerPeaks,
        stateFips,
        currentMetric,
        weeklyPeakKind
      );
      const abbr = FIPS_TO_ABBR[stateFips];
      if (!peak || !abbr) return;

      const value = Number(peak.value);
      if (!Number.isFinite(value)) return;

      const total = stateTotals
        ? (stateTotals[stateFips] || stateTotals[parseInt(stateFips)])
        : null;
      const percentOut = peak.percentOut !== null
        && peak.percentOut !== undefined
        && Number.isFinite(Number(peak.percentOut))
        ? Number(peak.percentOut)
        : (total ? (value / total) * 100 : 0);
      const percentText = ` (${percentOut.toFixed(2)}%)`;
      let plotValue = value;

      if (effectiveViewMode === 'percent') {
        plotValue = percentOut;
      } else if (value < 10000) {
        plotValue = 0.5;
      } else if (value < 50000) {
        plotValue = 1.5;
      } else if (value < 100000) {
        plotValue = 2.5;
      } else if (value < 500000) {
        plotValue = 3.5;
      } else {
        plotValue = 4.5;
      }

      locations.push(abbr);
      z.push(plotValue);

      if (isAuthenticated) {
        text.push(
          `${FIPS_TO_NAME[stateFips] || abbr}<br>`
          + `Peak Customers Out (${currentMetric} est.): ${Math.round(value).toLocaleString()}${percentText}<br>`
          + `Peak Date: ${formatPeakDate(peak.date)}`
        );
      } else {
        text.push(`${FIPS_TO_NAME[stateFips] || abbr}<br><i>Sign in to view figures</i>`);
      }
    });

    return { locations, z, text };
  };

  const plotlyData = isWeeklySummary ? getWeeklyPlotlyData() : getPlotlyData();

  const clearWeeklySelection = () => {
    lastWeeklyStateClickRef.current = { fips: null, timestamp: 0 };
    setRegionData(null);
    setCurrentRegion?.(null);
  };

  const handleWeeklyStateClick = (data) => {
    const point = data.points?.[0];
    const fips = point ? ABBR_TO_FIPS[point.location] : null;
    if (!fips) return;

    weeklyPointClickRef.current = true;
    setTimeout(() => {
      weeklyPointClickRef.current = false;
    }, 0);

    const state = {
      type: 'STATE',
      id: fips,
      name: FIPS_TO_NAME[fips] || point.location
    };

    if (!isAuthenticated) {
      onCountyClick(state);
      return;
    }

    const timestamp = Date.now();
    const browserClickCount = data.event?.detail || data.event?.originalEvent?.detail || 0;
    const previousClick = lastWeeklyStateClickRef.current;
    const isDoubleClick = browserClickCount > 1
      || (previousClick.fips === fips && timestamp - previousClick.timestamp < 350);

    if (isDoubleClick) {
      lastWeeklyStateClickRef.current = { fips: null, timestamp: 0 };
      const peak = getWeeklyCustomerPeak(
        weeklyCustomerPeaks,
        fips,
        currentMetric,
        weeklyPeakKind
      );
      onCountyClick({
        ...state,
        selectedDay: peak?.date || selectedDay
      });
      return;
    }

    lastWeeklyStateClickRef.current = { fips, timestamp };
    setRegionData(null);
    setCurrentRegion?.(state);
  };

  const summaryTitle = (() => {
    if (currentRegion && regionData?.countyData?.name) {
      return regionData.countyData.name;
    }
    if (typeof currentRegion === 'object' && currentRegion?.name) {
      return currentRegion.name;
    }
    if (stateId && stateId !== 'US') {
      return `${FIPS_TO_NAME[stateId.padStart(2, '0')] || 'State'} Summary`;
    }
    return 'U.S. Summary';
  })();
  const summaryTotalCustomers = (() => {
    const customerData = metricsData?.customers?.[currentMetric] || {};
    const selectedRegionId = typeof currentRegion === 'object' ? currentRegion?.id : currentRegion;
    if (selectedRegionId && stateId && stateId !== 'US') {
      const selectedValue = customerData[selectedRegionId] ?? currentRegion?.value;
      return typeof selectedValue === 'number' ? Math.round(selectedValue) : 0;
    }

    return Object.entries(customerData).reduce((total, [id, value]) => {
      if (stateId && stateId !== 'US' && !id.startsWith(stateId)) return total;
      return typeof value === 'number' ? total + value : total;
    }, 0);
  })();

  const summarySheetValue = (() => {
    if (!isAuthenticated) return 'Sign in';
    if (!isWeeklySummary) return summaryTotalCustomers.toLocaleString();

    const record = weeklySummary?.selectedRecord || weeklySummary?.leader;
    return record ? Math.round(record.value).toLocaleString() : 'N/A';
  })();

  return (
    <div className={`container-fluid mt-2 map-visual ${stateId && stateId !== 'US' ? 'is-state-view' : 'is-us-view'}`}>
      <div className="row">
        <div className="col-12 col-lg-8 map-visual-main">
          <div className="card border-secondary shadow-sm map-visual-card">
            <div className="card-header border-secondary bg-transparent map-visual-header">

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-1 map-visual-toolbar">
                <div className="d-flex align-items-center map-toolbar-mode" style={{ flex: '1 1 0%', minWidth: 'min-content' }}>
                  {isUsView && typeof setPredictionType === 'function' && (
                    <div className="btn-group" role="group" aria-label="Prediction period">
                      <button
                        type="button"
                        className={`btn ${predictionType === 'daily' ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                        aria-pressed={predictionType === 'daily'}
                        onClick={() => {
                          clearWeeklySelection();
                          setPredictionType('daily');
                        }}
                      >
                        <i className="fas fa-calendar-day me-1" aria-hidden="true"></i>
                        Daily view
                      </button>
                      <button
                        type="button"
                        className={`btn ${predictionType === 'weekly' ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                        aria-pressed={predictionType === 'weekly'}
                        onClick={() => {
                          clearWeeklySelection();
                          setPredictionType('weekly');
                        }}
                      >
                        <i className="fas fa-calendar-week me-1" aria-hidden="true"></i>
                        Weekly view
                      </button>
                    </div>
                  )}
                  {stateId && stateId !== 'US' && (
                    <div className="btn-group">
                      <button
                        className={`btn ${!isDurationView ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                        onClick={() => setIsDurationView(false)}
                      >
                        <i className="fas fa-bolt me-1"></i>
                        Outage
                      </button>

                      <button
                        className={`btn ${isDurationView ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                        onClick={() => setIsDurationView(true)}
                      >
                        <i className="fas fa-clock me-1"></i>
                        Duration
                      </button>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-center align-items-center map-toolbar-metric" style={{ flex: '1 1 0%', minWidth: 'min-content' }}>
                  <div className="btn-group me-2">
                    {['min', 'mean', 'max'].map(metric => (
                      <button
                        key={metric}
                        className={`btn ${currentMetric === metric ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                        onClick={() => setCurrentMetric(metric)}
                      >
                        <i className={`fas fa-${metric === 'min' ? 'arrow-down' : metric === 'max' ? 'arrow-up' : 'equals'} me-1`}></i>
                        {metric.charAt(0).toUpperCase() + metric.slice(1)}
                      </button>
                    ))}
                  </div>
                  <i
                    className="fas fa-info-circle text-muted"
                    style={{ cursor: 'pointer' }}
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="right"
                    data-bs-html="true"
                    title={isDurationView
                      ? `<strong>Duration Prediction:</strong><br/>
                          Min = shortest expected duration<br/>
                          Mean = average expected duration<br/>
                          Max = longest expected duration<br/><br/>
                          Use <b>Max</b> for worst-case planning.`
                      : `<strong>Outage Prediction:</strong><br/>
                          Min = best-case<br/>
                          Mean = expected<br/>
                          Max = worst-case<br/><br/>
                          Use <b>Max</b> for severe weather scenarios.`
                    }
                  />
                </div>

                <div className="d-flex justify-content-end align-items-center map-toolbar-view" style={{ flex: '1 1 0%', minWidth: 'min-content' }}>
                  {/* Duration sub-buttons hidden until feature is released */}
                  {isDurationView ? (
                    // <div className="btn-group" style={{ zIndex: 10 }}>
                    //   <button
                    //     className={`btn ${selectedDurationBucket === null ? 'btn-custom active' : 'btn-outline-custom'} px-2 py-1`}
                    //     style={{ fontSize: '12px' }}
                    //     onClick={() => setSelectedDurationBucket(null)}
                    //     title="Base Duration View"
                    //   >
                    //     <i className="fas fa-clock"></i>
                    //   </button>
                    //   {Object.entries(DURATION_SHORT_LABELS).map(([key, label]) => (
                    //     <button
                    //       key={key}
                    //       className={`btn ${selectedDurationBucket === key ? 'btn-custom active' : 'btn-outline-custom'} px-2 py-1`}
                    //       style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                    //       onClick={() => setSelectedDurationBucket(selectedDurationBucket === key ? null : key)}
                    //       title={`Probability Heatmap for ${label} hours`}
                    //     >
                    //       {label}
                    //     </button>
                    //   ))}
                    // </div>
                    <i
                      className="fas fa-info-circle text-muted"
                      style={{ cursor: 'pointer' }}
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-placement="left"
                      data-bs-html="true"
                      title={`
                          <strong>Duration Prediction:</strong><br/>
                          Duration values are probabilistic estimates of the chance<br/>
                          a customer experiences an outage for each time bucket.
                        `}
                    />
                  ) : (
                    <div className="btn-group" style={{ zIndex: 10 }}>
                      <button
                        className={`btn ${effectiveViewMode === 'percent' ? 'btn-custom active' : 'btn-outline-custom'}`}
                        onClick={() => setViewMode('percent')}
                        title="View by Percentage"
                      >
                        <i className="fas fa-percentage"></i>
                      </button>
                      <button
                        className={`btn ${effectiveViewMode === 'absolute' ? 'btn-custom active' : 'btn-outline-custom'}`}
                        onClick={() => setViewMode('absolute')}
                        title="View by Outage Counts"
                      >
                        <i className="fas fa-hashtag"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>


            </div>
            <div
              className="card-body p-0 position-relative map-visual-body"
              style={{ backgroundColor: 'var(--bg-modal)' }}
              onClick={() => {
                if (!isWeeklySummary || !selectedWeeklyStateId) return;
                if (weeklyPointClickRef.current) {
                  weeklyPointClickRef.current = false;
                  return;
                }
                clearWeeklySelection();
              }}
            >
              {(!stateId || stateId === 'US') ? (
                <Plot
                  data={[{
                    type: 'choropleth',
                    locationmode: 'USA-states',
                    locations: plotlyData.locations,
                    z: plotlyData.z,
                    text: plotlyData.text,
                    hoverinfo: 'text',
                    zmin: 0,
                    zmax: effectiveViewMode === 'percent' ? 10 : 5,
                    colorscale: effectiveViewMode === 'percent' ? [
                      [0, '#22c55e'],
                      [0.1, '#84cc16'],
                      [0.3, '#fbbf24'],
                      [0.5, '#f59e0b'],
                      [0.7, '#ef4444'],
                      [1, '#991b1b']
                    ] : [
                      [0, 'rgba(0,0,0,0)'],
                      [0.2, 'rgba(0,0,0,0)'],
                      [0.2, '#add8e6'],
                      [0.4, '#add8e6'],
                      [0.4, '#fbbf24'],
                      [0.6, '#fbbf24'],
                      [0.6, '#f87171'],
                      [0.8, '#f87171'],
                      [0.8, '#dc2626'],
                      [1, '#dc2626']
                    ],
                    autocolorscale: false,
                    marker: {
                      line: {
                        color: '#555',
                        width: 1
                      }
                    },
                    colorbar: {
                      title: {
                        text: isWeeklySummary
                          ? (effectiveViewMode === 'percent' ? 'Peak Impact %' : 'Peak Customers Out')
                          : (effectiveViewMode === 'percent' ? 'Impact %' : 'Count'),
                        font: { color: theme === 'dark' ? 'white' : 'black' }
                      },
                      thickness: isMobileViewport ? 8 : 10,
                      x: isMobileViewport ? 0.96 : 0.98,
                      y: isMobileViewport ? 0.86 : 0.02,
                      len: isMobileViewport ? 0.22 : 0.4,
                      xanchor: 'right',
                      yanchor: isMobileViewport ? 'top' : 'bottom',
                      bgcolor: theme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                      tickfont: { color: theme === 'dark' ? 'white' : 'black' },
                      tickmode: effectiveViewMode === 'absolute' ? 'array' : 'auto',
                      tickvals: effectiveViewMode === 'absolute' ? [0.5, 1.5, 2.5, 3.5, 4.5] : undefined,
                      ticktext: effectiveViewMode === 'absolute' ? ['<10k', '10k-50k', '50k-100k', '100k-500k', '500k+'] : undefined,
                    }
                  }]}
                  layout={{
                    geo: {
                      scope: 'usa',
                      projection: { type: 'albers usa' },
                      showlakes: false,
                      bgcolor: theme === 'dark' ? '#222' : '#F8FAFC',
                      lakecolor: theme === 'dark' ? '#333' : '#e2e8f0',
                      landcolor: theme === 'dark' ? '#222' : '#fff',
                      subunitcolor: theme === 'dark' ? '#444' : '#ccc'
                    },
                    paper_bgcolor: theme === 'dark' ? '#222' : '#ffffff',
                    plot_bgcolor: theme === 'dark' ? '#222' : '#ffffff',
                    margin: { l: 0, r: 0, t: 0, b: 0 },
                    dragmode: false,
                    autosize: true
                  }}
                  style={{ width: '100%', height: '100%' }}
                  useResizeHandler={true}
                  config={{ displayModeBar: false, responsive: true, topojsonURL: process.env.PUBLIC_URL + '/' }}
                  onClick={(data) => {
                    if (isWeeklySummary) {
                      handleWeeklyStateClick(data);
                      return;
                    }
                    if (data.points && data.points[0]) {
                      const abbr = data.points[0].location;
                      const fips = ABBR_TO_FIPS[abbr];
                      if (fips) {
                        onCountyClick({ type: 'STATE', id: fips, name: abbr });
                      }
                    }
                  }}
                />
              ) : (
                <div
                  ref={mapRef}
                  className="map-canvas"
                  style={{
                    height: '100%',
                    width: '100%',
                    backgroundColor: 'var(--bg-modal)'
                  }}
                ></div>
              )}

              {/* Guest lock overlay: shown on US map only */}
              {!isAuthenticated && (!stateId || stateId === 'US') && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowSignInModal(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 14px',
                      borderRadius: 20,
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      background: theme === 'dark'
                        ? 'rgba(15,23,42,0.82)'
                        : 'rgba(255,255,255,0.88)',
                      color: theme === 'dark' ? '#93c5fd' : '#1d4ed8',
                      backdropFilter: 'blur(6px)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                      border: '1px solid rgba(99,102,241,0.3)',
                      letterSpacing: '0.02em',
                      cursor: 'pointer',
                    }}
                  >
                    <i className="fas fa-lock" style={{ fontSize: '11px' }} />
                    Sign in to view county-level forecasts.
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Region Summary Column */}
        <div className="col-12 col-lg-4 map-summary-desktop">
          {isWeeklySummary ? (
            <WeeklyRegionSummary summary={weeklySummary} isAuthenticated={isAuthenticated} />
          ) : (
            <RegionSummary
              currentRegion={currentRegion}
              currentMetric={currentMetric}
              regionData={regionData}
              currentDataType={currentDataType}
              metricsData={metricsData}
              countiesList={countiesList}
              statesList={statesList}
              stateId={stateId}
              isWeatherSummaryLoading={isWeatherSummaryLoading}
              filteredDurationData={filteredDurationData}
              isAuthenticated={isAuthenticated}
              selectedDay={selectedDay}
              freeTopStatesCustomersOut={freeTopStatesCustomersOut}
              onRegionSelect={setCurrentRegion}
            />
          )}
        </div>
      </div>
      {isMobileViewport && (
        <div className={`map-summary-sheet ${isSummarySheetOpen ? 'is-open' : ''}`} aria-label="Region summary">
          <button
            type="button"
            className="map-summary-sheet-toggle"
            onClick={() => setIsSummarySheetOpen(prev => !prev)}
            aria-controls="map-summary-sheet-content"
            aria-expanded={isSummarySheetOpen}
          >
            <span className="map-summary-sheet-handle" aria-hidden="true"></span>
            <span className="map-summary-sheet-title">{summaryTitle}</span>
            <span className="map-summary-sheet-value">
              {summarySheetValue}
            </span>
            <i className={`fas fa-chevron-${isSummarySheetOpen ? 'down' : 'up'}`} aria-hidden="true"></i>
          </button>
          <div id="map-summary-sheet-content" className="map-summary-sheet-content" aria-hidden={!isSummarySheetOpen}>
            {isWeeklySummary ? (
              <WeeklyRegionSummary summary={weeklySummary} isAuthenticated={isAuthenticated} />
            ) : (
              <RegionSummary
                currentRegion={currentRegion}
                currentMetric={currentMetric}
                regionData={regionData}
                currentDataType={currentDataType}
                metricsData={metricsData}
                countiesList={countiesList}
                statesList={statesList}
                stateId={stateId}
                isWeatherSummaryLoading={isWeatherSummaryLoading}
                filteredDurationData={filteredDurationData}
                isAuthenticated={isAuthenticated}
                selectedDay={selectedDay}
                freeTopStatesCustomersOut={freeTopStatesCustomersOut}
                onRegionSelect={setCurrentRegion}
              />
            )}
          </div>
        </div>
      )}
      <SignInPromptModal show={showSignInModal} onHide={() => setShowSignInModal(false)} />
    </div>
  );
};

const WeeklyRegionSummary = ({ summary, isAuthenticated }) => {
  const selectedRecord = summary?.selectedRecord || null;
  const record = selectedRecord || summary?.leader || null;
  const title = selectedRecord ? `${selectedRecord.name} Summary` : 'U.S. Summary';
  const lockedValue = (
    <span
      className="d-flex align-items-center gap-1 small"
      style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
    >
      <i className="fas fa-lock" style={{ fontSize: '11px' }} />
      Sign in to view
    </span>
  );

  return (
    <div className="d-flex flex-column gap-3 h-100">
      <div className="card shadow-sm flex-grow-1 border-secondary">
        <div className="card-header border-secondary bg-transparent">
          <h5 className="mb-0 fw-bold text-main">{title}</h5>
        </div>
        <div className="card-body">
          {!selectedRecord && (
            <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
              <span className="text-muted">Highest State Peak</span>
              {isAuthenticated ? (
                <span className="fw-bold text-main text-end">{record?.name || 'N/A'}</span>
              ) : lockedValue}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
            <span className="text-muted">Peak Customers Out ({summary?.metricLabel})</span>
            {isAuthenticated ? (
              <span className="fw-bold fs-5 text-main text-end">
                {record ? Math.round(record.value).toLocaleString() : 'N/A'}
              </span>
            ) : lockedValue}
          </div>

          {isAuthenticated && record?.percentOut !== null && record?.percentOut !== undefined && (
            <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
              <span className="text-muted">Impact %</span>
              <span className="fw-semibold text-main text-end">{record.percentOut.toFixed(2)}%</span>
            </div>
          )}

          {isAuthenticated && (
            <div className="d-flex justify-content-between align-items-center gap-3">
              <span className="text-muted">Peak Date</span>
              <span className="fw-semibold text-main text-end">{formatPeakDate(record?.date)}</span>
            </div>
          )}

          <div className="text-center text-muted">
            <i className="fas fa-map-marker-alt fa-2x mb-3 text-secondary opacity-50"></i>
            <p className="mb-0">
              {selectedRecord
                ? 'Double tap this state to open the county-level view.'
                : 'Select a state on the map for its weekly peak summary.'}
            </p>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-secondary">
        <div className="card-header border-secondary bg-transparent">
          <h6 className="mb-0 fw-bold text-main">Top 5 States by Customers Out</h6>
        </div>
        <div className="card-body p-0">
          <ul className="list-group list-group-flush">
            {(summary?.topStates || []).map((state, index) => (
              <li
                key={state.id}
                className="list-group-item d-flex justify-content-between align-items-center bg-transparent border-secondary"
              >
                <span className="text-truncate me-2" title={state.name}>
                  {index + 1}. {state.name}
                </span>
                {isAuthenticated ? (
                  <span className="bg-primary text-white px-1 py-0.25 rounded-pill">
                    {Math.round(state.value).toLocaleString()}
                  </span>
                ) : (
                  <span
                    className="d-flex align-items-center gap-1 small"
                    style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
                  >
                    <i className="fas fa-lock" style={{ fontSize: '11px' }} />
                    Sign in
                  </span>
                )}
              </li>
            ))}
            {(!summary?.topStates || summary.topStates.length === 0) && (
              <li className="list-group-item text-muted bg-transparent">No data available</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Region Summary Component
const RegionSummary = ({ currentRegion, currentMetric, regionData, currentDataType, metricsData, countiesList, statesList, stateId, isWeatherSummaryLoading, filteredDurationData, isAuthenticated = true, selectedDay, freeTopStatesCustomersOut = [], onRegionSelect }) => {

  // Duration display config
  const DURATION_BUCKETS = [
    { key: 'up_to_12h', label: '0–12h' },
    { key: 'h12_24', label: '12–24h' },
    { key: 'h24_72', label: '24–72h' },
    { key: 'gt_72h', label: '> 72h' }
  ];

  const DURATION_COLORS = {
    up_to_12h: '#22c55e',
    h12_24: '#f59e0b',
    h24_72: '#f87171',
    gt_72h: '#dc2626'
  };

  // Calculate totals dynamically from metricsData
  const calculateTotals = () => {
    if (!metricsData || !metricsData[currentDataType] || !metricsData[currentDataType][currentMetric]) {
      return { totalCustomers: 0, totalOutages: 0 };
    }

    let totalCustomers = 0;
    let totalOutages = 0;

    // Sum all region values
    const customerData = metricsData.customers[currentMetric];
    Object.entries(customerData || {}).forEach(([id, value]) => {
      // If in State view, only sum counties for that state
      if (stateId && stateId !== 'US' && !id.startsWith(stateId)) {
        return;
      }
      if (typeof value === 'number') totalCustomers += value;
    });

    const outageData = metricsData.outages[currentMetric];
    Object.entries(outageData || {}).forEach(([id, value]) => {
      // If in State view, only sum counties for that state
      if (stateId && stateId !== 'US' && !id.startsWith(stateId)) {
        return;
      }
      if (typeof value === 'number') totalOutages += value;
    });

    return {
      totalCustomers: Math.round(totalCustomers),
      totalOutages: Math.round(totalOutages)
    };
  };

  const { totalCustomers, totalOutages } = calculateTotals();

  // Helper to get Top 5 Counties / States
  const getTopCounties = () => {
    if (!isAuthenticated && (!stateId || stateId === 'US')) {
      const selectedEntry = freeTopStatesCustomersOut.find((entry) => entry?.date?.startsWith(selectedDay))
        || freeTopStatesCustomersOut[0];
      const freeStates = Array.isArray(selectedEntry?.[currentMetric]) ? selectedEntry[currentMetric] : [];
      return freeStates.slice(0, 5).map((state) => {
        const stateFips = String(state?.state_fips || '').padStart(2, '0');
        return {
          id: stateFips,
          val: null,
          name: FIPS_TO_NAME[stateFips] || `State ${stateFips}`
        };
      });
    }

    if (!metricsData || !metricsData.customers || !metricsData.customers[currentMetric]) return [];

    const dataMap = metricsData.customers[currentMetric];

    // Convert to array of { id, val }
    const sorted = Object.entries(dataMap)
      .filter(([id, val]) => {
        // Filter out counties not in current state if we are in state view
        if (stateId && stateId !== 'US') {
          return id.startsWith(stateId);
        }
        return true;
      })
      .map(([id, val]) => {
        // Try to find name in counties or states
        let name = `Region ${id}`;
        if (id.length <= 2) {
          // It is a State. Use safe hardcoded lookup first, then fallback to statesList.
          // This fixes "Region 06" -> "California"
          let fipsPadded = id.toString().padStart(2, '0');
          if (FIPS_TO_NAME[fipsPadded]) {
            name = FIPS_TO_NAME[fipsPadded];
          } else if (statesList) {
            const s = statesList.find(st => st.fips.toString() === fipsPadded);
            if (s) name = s.name;
          }
        } else if (countiesList) {
          const c = countiesList.find(cnt => cnt.fips.toString() === id.toString());
          if (c) name = c.name;
        }

        return { id, val, name };
      })
      .sort((a, b) => b.val - a.val)
      .slice(0, 5);

    return sorted;
  };

  const topCounties = getTopCounties();

  const customerPercentage = regionData && regionData.customers && regionData.customers[currentMetric] && totalCustomers > 0
    ? Math.round((regionData.customers[currentMetric] / totalCustomers) * 100)
    : (regionData ? 0 : 0);

  const outagePercentage = regionData && regionData.outages && regionData.outages[currentMetric] && totalOutages > 0
    ? Math.round((regionData.outages[currentMetric] / totalOutages) * 100)
    : (regionData ? 0 : 0);

  function getStateSummaryTitle() {
    // Priority 1: State Name (if selected and not US)
    if (stateId && stateId !== 'US') {
      const stateName = FIPS_TO_NAME[stateId.padStart(2, '0')] || 'State';
      return `${stateName} Summary`;
    }
    // Priority 2: County Name (if clicked in US view?)
    if (currentRegion && regionData?.countyData?.name) {
      return regionData.countyData.name;
    }
    return "U.S. Summary";
  }

  return (
    <div className="d-flex flex-column gap-3 h-100">

      {/* Selected Region / Summary Box */}
      <div className="card shadow-sm flex-grow-1 border-secondary">
        <div className="card-header border-secondary bg-transparent">
          <h5 className="mb-0 fw-bold text-main">
            {getStateSummaryTitle()}
          </h5>
        </div>
        <div className="card-body">
          {/* Statistics (Statewide / Aggregate) */}
          <div className="mb-4">
            {/* Removed Statewise Impact Header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Total Customers Out</span>
              {isAuthenticated ? (
                <span className="fw-bold fs-5 text-main">{totalCustomers.toLocaleString()}</span>
              ) : (
                <span
                  className="d-flex align-items-center gap-1 small"
                  style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
                >
                  <i className="fas fa-lock" style={{ fontSize: '11px' }} />
                  Sign in to view
                </span>
              )}
            </div>
          </div>

          {/* Weather Parameters - hidden for guests */}
          {isAuthenticated && regionData && (isWeatherSummaryLoading || regionData.weatherData) && (
            <div>
              <h6 className="text-muted text-uppercase mt-4 mb-2" style={{ letterSpacing: '1px' }}>
                {regionData.countyData?.name || 'Selected County'} County Weather Summary
              </h6>

              <div className="region-weather-summary rounded p-3 border border-secondary mt-3" style={{ minHeight: '200px', backgroundColor: 'var(--bg-hover)' }}>
                {isWeatherSummaryLoading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100" style={{ minHeight: '160px' }}>
                    <div className="spinner-border text-primary mb-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted small">Loading weather data...</span>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {/* Temp */}
                    <div className="region-weather-row d-flex justify-content-between border-bottom border-secondary pb-1">
                      <span className="text-muted small">Temperature</span>
                      <span className="fw-bold text-main small">
                        Min: {regionData.weatherData.min_temperature?.value ?? '--'} {regionData.weatherData.min_temperature?.unit} /
                        Max: {regionData.weatherData.max_temperature?.value ?? '--'} {regionData.weatherData.max_temperature?.unit}
                      </span>
                    </div>

                    {/* Wind */}
                    <div className="region-weather-row d-flex justify-content-between border-bottom border-secondary pb-1">
                      <span className="text-muted small">Max Wind Gusts</span>
                      <span className="fw-bold text-main small">
                        {regionData.weatherData.max_wind_gusts?.value ?? '--'} {regionData.weatherData.max_wind_gusts?.unit}
                      </span>
                    </div>
                    <div className="region-weather-row d-flex justify-content-between border-bottom border-secondary pb-1">
                      <span className="text-muted small">Avg Wind Speed</span>
                      <span className="fw-bold text-main small">
                        {regionData.weatherData.avg_wind_spd?.value ?? '--'} {regionData.weatherData.avg_wind_spd?.unit}
                      </span>
                    </div>
                    <div className="region-weather-row d-flex justify-content-between border-bottom border-secondary pb-1">
                      <span className="text-muted small">Avg Wind Dir</span>
                      <span className="fw-bold text-main small">
                        {regionData.weatherData.avg_wind_dir?.value ?? '--'} {regionData.weatherData.avg_wind_dir?.unit}
                      </span>
                    </div>

                    {/* Precip */}
                    <div className="region-weather-row d-flex justify-content-between border-bottom border-secondary pb-1">
                      <span className="text-muted small">Precipitation</span>
                      <span className="fw-bold text-main small">
                        {regionData.weatherData.precipitation?.value ?? '--'} {regionData.weatherData.precipitation?.unit}
                      </span>
                    </div>
                    <div className="region-weather-row d-flex justify-content-between border-bottom border-secondary pb-1">
                      <span className="text-muted small">Precip Probability Max</span>
                      <span className="fw-bold text-main small">
                        {regionData.weatherData.precipitation_probability_max?.value ?? '--'} {regionData.weatherData.precipitation_probability_max?.unit}
                      </span>
                    </div>

                    {/* Code */}
                    <div className="region-weather-row d-flex justify-content-between pt-1">
                      <span className="text-muted small">Weather Code</span>
                      <span className="fw-bold text-main small text-end">
                        {regionData.weatherData.weather_code?.value ?? 'N/A'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Outage Duration Section - UNHIDDEN */}
          {isAuthenticated && (() => {
            // currentRegion can be an object {id, name, ...} or a string FIPS
            const countyFips = typeof currentRegion === 'string' ? currentRegion : currentRegion?.id;
            const durationEntry = countyFips && filteredDurationData ? filteredDurationData[countyFips] : null;
            const durationProbs = durationEntry ? durationEntry[currentMetric] : null;

            if (!countyFips || !durationProbs) return null;

            return (
              <div>
                <h6 className="text-muted text-uppercase mt-4 mb-2" style={{ letterSpacing: '1px' }}>
                  Outage Duration : {regionData?.countyData?.name || 'Selected County'}
                </h6>
                <p className="text-muted small">
                  Estimated probability of outage duration for the selected county.
                </p>
                <div className="rounded p-3 border border-secondary mt-2" style={{ backgroundColor: 'var(--bg-hover)' }}>
                  <div className="d-flex flex-column gap-2">
                    {DURATION_BUCKETS.map(bucket => {
                      const prob = durationProbs[bucket.key];
                      const pct = prob != null ? (prob * 100) : 0;
                      const isMax = DURATION_BUCKETS.every(b =>
                        (durationProbs[b.key] || 0) <= prob
                      );
                      return (
                        <div key={bucket.key}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="small fw-semibold" style={{ minWidth: '55px', color: 'var(--text-main)' }}>{bucket.label}</span>
                            <span className="small fw-bold" style={{ color: isMax ? DURATION_COLORS[bucket.key] : 'var(--text-muted)' }}>
                              {pct.toFixed(1)}%
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: 'var(--bg-modal)',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${Math.min(pct, 100)}%`,
                              height: '100%',
                              backgroundColor: DURATION_COLORS[bucket.key],
                              borderRadius: '4px',
                              transition: 'width 0.4s ease'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-muted small mt-2 text-end" style={{ fontStyle: 'italic' }}>
                    Showing <strong>{currentMetric}</strong> estimate
                  </div>
                </div>
              </div>
            );
          })()}


          {!currentRegion && (
            <div className="mt-5 text-center text-muted">
              <i className="fas fa-map-marker-alt fa-2x mb-3 text-secondary opacity-50"></i>
              <p>Select a region on the map for detailed local insights.</p>
            </div>
          )}
        </div>
      </div>

      {/* Top 5 Box */}
      <div className="card shadow-sm border-secondary">
        <div className="card-header border-secondary bg-transparent">
          <h6 className="mb-0 fw-bold text-main">Top 5 {(!stateId || stateId === 'US') ? 'States' : 'Counties'} by Customers Out</h6>
        </div>
        <div className="card-body p-0">
          <ul className="list-group list-group-flush">
            {topCounties.map((county, idx) => {
              const isUsView = !stateId || stateId === 'US';
              const isClickable = !isUsView && county.val !== null && onRegionSelect;
              const selectedRegionId = typeof currentRegion === 'string' ? currentRegion : (currentRegion?.id ?? null);
              const isActive = !isUsView && selectedRegionId && String(selectedRegionId) === String(county.id);
              return (
              <li
                key={county.id}
                className={`list-group-item d-flex justify-content-between align-items-center bg-transparent border-secondary${isClickable ? ' stats-rank-item' : ''}${isActive ? ' active' : ''}`}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? () => onRegionSelect(county.id) : undefined}
                onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRegionSelect(county.id); } } : undefined}
                aria-pressed={isClickable ? isActive : undefined}
              >
                <span>{idx + 1}. {county.name}</span>
                {county.val === null ? (
                  <span
                    className="d-flex align-items-center gap-1 small"
                    style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
                  >
                    <i className="fas fa-lock" style={{ fontSize: '11px' }} />
                    Sign in
                  </span>
                ) : (
                  <span className="bg-primary text-white px-1 py-0.25 rounded-pill">{county.val.toLocaleString()}</span>
                )}
              </li>
              );
            })}
            {topCounties.length === 0 && <li className="list-group-item text-muted">No data available</li>}
          </ul>
        </div>
      </div>

    </div >
  );
};

export default MapVisual;   
